import { CSSProperties, useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent, logException } from "../../../../helper/AppInsightsHelper";
import { CustomShimmer } from "../../../../helper/CustomShimmer";
import { useGetProductFeatureRequestDataQuery } from "../../../../slices/ExploreProducts.slice";
import { IFeatureRequestSummary } from "../../../../interfaces/CloseTheLoop.interfaces";
import FeatureChart from "../../../FeatureRequest/FeatureChart";
import FeatureTable from "../../../FeatureRequest/FeatureTable";
import { MetricOptionProps } from "../../../../SharedComponents/Interfaces/CustomCardWithMetrics.interfaces";
import { PRODUCT_VIEW_DATA } from "../../Common/ProductView.constants";
import { ProductViewFiltersContext } from "../../../../pages/ProductView";

/**
 * Y-axis tick formatter for the "feature requests created" trend line. The values
 * are integer counts, so render whole numbers only (the shared LineChartComponent's
 * default SI formatter renders "1.0, 2.0, ..." which is misleading for counts).
 * Non-integer ticks (should not occur for a count axis) are left unlabeled.
 */
const formatWholeNumberTick = (value: any): string => {
  const n = Number(value);
  return Number.isInteger(n) ? n.toLocaleString("en-US") : "";
};

/**
 * Product View - Feature Request tab.
 *
 * Sources feature requests from the Close The Loop feature evidences
 * (Kusto Fn_S360_CTL_getFeatureEvidences, all sources: CRM/UAT/CSS) scoped to the
 * currently selected product, and presents them with the shared client-side grid
 * and charts used by the Customer View Feature Request tab. A single product's set
 * is bounded, so we fetch it in one call and paginate/filter client-side.
 */
export const FeatureRequestTab = () => {
  const [searchParams] = useSearchParams();
  const productName: string = searchParams.get("productname") ?? PRODUCT_VIEW_DATA.PRODUCT_VIEW.DEFAULT_PRODUCT_NAME;
  const filters = useContext(ProductViewFiltersContext);

  // Chart filters as an array to handle multiple simultaneous filters (e.g. source + status)
  const [chartFilters, setChartFilters] = useState<
    Array<{
      filterKey: string;
      value: string;
    }>
  >([]);

  const featureRequestData = useGetProductFeatureRequestDataQuery(
    {
      productName,
      page: 1,
      pageSize: 1000000,
      sortBy: "lastChangedDate",
      sortDirection: "desc"
    },
    { skip: !productName }
  );

  // Augment each row with a month bucket (end-of-month) derived from latestCreatedDate,
  // so the trend line can group feature requests created per month. End-of-month is used
  // to stay consistent with every other line chart in the app (COSOC "endOfMonth",
  // Consumption/Revenue "endOfMonth", Support "closedMonthYear" are all end-of-month dates).
  const allRows: IFeatureRequestSummary[] = useMemo(() => {
    const rows = featureRequestData?.data?.featureRequests ?? [];
    return rows.map((row) => {
      // The API returns latestCreatedDate as "dd-MM-yyyy". Parse it explicitly:
      // new Date("09-06-2026") would misread it as MM-DD-YYYY (Sep 6) or return
      // an Invalid Date for days > 12, corrupting the monthly trend buckets.
      const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec((row.latestCreatedDate ?? "").trim());
      let createdMonth: string | null = null;
      if (match) {
        const year = Number(match[3]);
        const month = Number(match[2]); // 1-based
        // new Date(year, month, 0) yields the last day of the given (1-based) month.
        const lastDay = String(new Date(year, month, 0).getDate()).padStart(2, "0");
        createdMonth = `${match[3]}-${match[2]}-${lastDay}`;
      }
      return { ...row, createdMonth } as IFeatureRequestSummary;
    });
  }, [featureRequestData?.data?.featureRequests]);

  // Apply chart-driven filters (client-side)
  const filteredRows = useMemo(() => {
    if (chartFilters.length === 0) {
      return allRows;
    }
    return allRows.filter((row) =>
      chartFilters.every((filter) => String((row as any)[filter.filterKey] ?? "") === filter.value)
    );
  }, [allRows, chartFilters]);

  const customProps = {
    productName: filters?.productName ?? productName,
    productFilters: JSON.stringify(filters)
  };

  // Memoize the chart options so the object identity is stable across renders.
  // CustomCardWithMetrics recomputes its charts whenever this object's identity
  // changes and, for the line chart, updates internal state during that compute;
  // recreating the object every render would loop and make the trend chart flicker.
  const chartConfig = useMemo<MetricOptionProps<any>>(
    () => ({
      filteredData: filteredRows,
      originalData: allRows,
      chartOptions: [
        {
          id: 0,
          field: "createdMonth",
          key: "product-view-feature-request-chart-created-trend",
          title: "How are feature requests trending over time?",
          type: "line",
          legendName: "Feature Requests Created",
          xaxisTitle: "Created Date",
          yaxisTitle: "Number of Feature Requests",
          yAxisTickFormat: formatWholeNumberTick,
          tooltipDescription: "Number of feature requests created each month, based on their latest created date."
        },
        {
          id: 1,
          field: "priority",
          key: "product-view-feature-request-chart-by-priority",
          title: "Feature Request By Priority",
          type: "donut"
        },
        {
          id: 2,
          field: "source",
          key: "product-view-feature-request-chart-by-source",
          title: "Feature Request By Source",
          type: "donut"
        },
        {
          id: 3,
          field: "status",
          key: "product-view-feature-request-chart-by-status",
          title: "Feature Request By Feature Status",
          type: "donut"
        }
      ]
    }),
    [filteredRows, allRows]
  );

  // Handler to manage the array of chart filters
  const handleChartFilter = (newFilter: { filterKey: string; value: string }) => {
    setChartFilters((prevFilters) => {
      // If filterKey or value is empty, reset all filters
      if (!newFilter.filterKey || !newFilter.value) {
        return [];
      }

      const existingFilterIndex = prevFilters.findIndex((f) => f.filterKey === newFilter.filterKey);

      if (existingFilterIndex === -1) {
        return [...prevFilters, newFilter];
      }
      const updatedFilters = [...prevFilters];
      updatedFilters[existingFilterIndex] = newFilter;
      return updatedFilters;
    });
  };

  const showNoData = () => {
    const rootStyle: CSSProperties = {
      alignItems: "center",
      display: "flex",
      fontSize: 18,
      fontWeight: 600,
      justifyContent: "center",
      padding: 200
    };
    return <div style={rootStyle}>{`Feature request details are not available for ${productName}.`}</div>;
  };

  const HandleAPIExcpetion = () => {
    const enableAPIException = featureRequestData.error;
    return (enableAPIException && <DisplayAPIsException error={enableAPIException} height={250} />) || "";
  };

  // Hooks
  useEffect(() => {
    logEvent("Feature Request", "Product View Page", "FeatureRequestTab", customProps);
  }, []);

  useEffect(() => {
    if (featureRequestData.error) {
      logException(
        "Product View Page - Feature Request Tab",
        "FeatureRequestTab",
        "useGetProductFeatureRequestDataQuery",
        featureRequestData.error,
        customProps
      );
    }
  }, [featureRequestData.error]);

  return (
    <>
      <FeatureChart
        loading={featureRequestData.isLoading}
        onSelectedChartFilter={handleChartFilter}
        tenantId=""
        tenantName={productName}
        showAIInsights={false}
        bannerMessage="This view shows feature requests from all sources (CRM, UAT, CSS)."
        options={chartConfig}
      />
      {featureRequestData.isLoading ? (
        <CustomShimmer columnCount={10} />
      ) : (
        (featureRequestData.isSuccess && allRows.length > 0 && (
          <div style={{ marginLeft: 8, marginRight: 8 }}>
            <FeatureTable rows={filteredRows} loading={featureRequestData.isFetching} tenantName={productName} />
          </div>
        )) ||
        (featureRequestData.isSuccess && allRows.length === 0 && showNoData())
      )}
      <HandleAPIExcpetion />
    </>
  );
};
